import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import { Crop, Maximize, RotateCcw, X } from 'lucide-react';
import { drawProductImage, exportProductImage } from './productImageCanvas';
import 'react-image-crop/dist/ReactCrop.css';
import './ProductImageCropper.css';

const ProductImageCropper = ({ source, label, onCancel, onApply }) => {
  const [src, setSrc] = useState('');
  const [mode, setMode] = useState('fit');
  const [crop, setCrop] = useState();
  const [scale, setScale] = useState(100);
  const [background, setBackground] = useState('transparent');
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const imageRef = useRef(null);
  const previewRef = useRef(null);
  const dialogRef = useRef(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  useEffect(() => {
    const objectUrl = typeof source === 'string' ? null : URL.createObjectURL(source);
    setSrc(objectUrl || source);
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [source]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const initialCrop = (image) => centerCrop(makeAspectCrop({ unit: '%', width: 100 }, 1, image.width, image.height), image.width, image.height);

  const reset = () => {
    setMode('fit');
    setScale(100);
    setBackground('transparent');
    if (imageRef.current) setCrop(initialCrop(imageRef.current));
    setError('');
  };

  useEffect(() => {
    if (!ready || !imageRef.current || !previewRef.current) return;
    try {
      drawProductImage(previewRef.current, imageRef.current, { mode, crop, scale, background }, 480);
    } catch (err) {
      setError(err.message);
    }
  }, [ready, mode, crop, scale, background]);

  const apply = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    try {
      const file = await exportProductImage(imageRef.current, { mode, crop, scale, background }, typeof source === 'string' ? 'product' : source.name);
      await onApply(file);
    } catch (err) {
      setError(err.message || 'Could not upload this image. Please try again.');
      setBusy(false);
    }
  };

  const handleKeyDown = (event) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      if (!busy) cancelRef.current();
    }
    if (event.key !== 'Tab') return;
    const controls = Array.from(dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]'));
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) {
      event.preventDefault();
      first?.focus();
    }
  };

  return createPortal(
    <div className="product-crop-overlay" onClick={event => event.stopPropagation()}>
      <section className="product-crop-dialog" role="dialog" aria-modal="true" aria-labelledby="product-crop-title" tabIndex={-1} ref={dialogRef} onKeyDown={handleKeyDown}>
        <header className="product-crop-header">
          <div>
            <h3 id="product-crop-title">Product Image</h3>
            <span>{label}</span>
          </div>
          <button type="button" className="product-crop-icon" title="Close crop editor" aria-label="Close crop editor" disabled={busy} onClick={onCancel}><X size={20} /></button>
        </header>
        <div className="product-crop-body">
          <div className="product-crop-toolbar">
            <div className="product-crop-modes" role="group" aria-label="Image framing">
              <button type="button" aria-pressed={mode === 'fit'} disabled={busy} onClick={() => setMode('fit')}><Maximize size={16} /> Fit Entire Image</button>
              <button type="button" aria-pressed={mode === 'crop'} disabled={busy || !ready} onClick={() => setMode('crop')}><Crop size={16} /> Custom Crop</button>
            </div>
            <button type="button" className="product-crop-icon" title="Reset framing" aria-label="Reset framing" disabled={busy || !ready} onClick={reset}><RotateCcw size={18} /></button>
          </div>
          <div className="product-crop-grid">
            <div className="product-crop-source">
              {!ready && !error && <span role="status">Loading image...</span>}
              <ReactCrop crop={crop} onChange={(_, percentCrop) => setCrop(percentCrop)} aspect={1} minWidth={20} minHeight={20} keepSelection ruleOfThirds disabled={mode === 'fit' || busy} className={mode === 'fit' ? 'product-crop-fit' : ''}>
                {src && <img ref={imageRef} src={src} crossOrigin="anonymous" alt="Product crop source" onLoad={event => {
                  setCrop(initialCrop(event.currentTarget));
                  setReady(true);
                  setError('');
                }} onError={() => {
                  setReady(false);
                  setError('This image could not be opened. Upload the image file to crop it.');
                }} />}
              </ReactCrop>
            </div>
            <div className="product-crop-preview-wrap">
              <h4>Shop Preview</h4>
              <canvas ref={previewRef} className="product-crop-preview" aria-label="Square product image preview" />
            </div>
          </div>
          {mode === 'fit' && <label className="product-crop-scale">Image Size <output>{scale}%</output>
            <input type="range" min="60" max="100" step="1" value={scale} disabled={busy || !ready} onChange={event => setScale(Number(event.target.value))} />
          </label>}
          <div className="product-crop-backgrounds" role="group" aria-label="Image background">
            <span>Background</span>
            {[['transparent', 'Transparent'], ['#0a0a0a', 'Dark'], ['#ffffff', 'White']].map(([value, name]) => <button key={value} type="button" aria-label={`${name} background`} title={`${name} background`} aria-pressed={background === value} disabled={busy} className={value === 'transparent' ? 'product-crop-transparent' : ''} style={value === 'transparent' ? undefined : { backgroundColor: value }} onClick={() => setBackground(value)} />)}
          </div>
          {error && <div className="product-crop-error" role="alert">{error}</div>}
        </div>
        <footer className="product-crop-actions">
          <button type="button" className="admin-button" disabled={busy} onClick={onCancel}>Cancel</button>
          <button type="button" className="admin-button primary" disabled={busy || !ready} onClick={apply}>{busy ? 'Uploading...' : 'Apply Image'}</button>
        </footer>
      </section>
    </div>, document.body
  );
};

export default ProductImageCropper;
